import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Paper,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../config';
import SchoolIcon from '@mui/icons-material/School';
import CampaignIcon from '@mui/icons-material/Campaign';
import PrintIcon from '@mui/icons-material/Print';
import LockIcon from '@mui/icons-material/Lock';
import handsImage from '../../assets/Home/Hands.jpg';
import ePublisherImage from '../../assets/Home/ePublisher_symbol_650x680px.png';
import advertisingImage from '../../assets/Home/Advertising Graphic_Multicol.png';
import printDesignImage from '../../assets/Design+Print/Design + Print Graphic_Multicol.png';
import logo from '../../assets/Home/Austnews-Logo-Primary-large-mwsdyymnooerpyjob0leexg6pkn4fwwenomwx6ti9k.png';

const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [formData, setFormData] = useState({
    contactName: '',
    schoolOrBusinessName: '',
    phoneNumber: '',
    emailAddress: '',
    service: '',
    message: '',
    howDidYouHear: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    const enquiryData = {
      contactName: formData.contactName.trim(),
      schoolOrBusinessName: formData.schoolOrBusinessName.trim(),
      phone: formData.phoneNumber.trim(),
      email: formData.emailAddress.trim(),
      service: formData.service,
      message: formData.message.trim(),
      howDidYouHear: formData.howDidYouHear
    };

    try {
      const response = await fetch(`${API_URL}/website-general-enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(enquiryData)
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        setSubmitSuccess(true);
        setFormData({
          contactName: '',
          schoolOrBusinessName: '',
          phoneNumber: '',
          emailAddress: '',
          service: '',
          message: '',
          howDidYouHear: ''
        });
        window.scrollTo({ top: document.getElementById('contact')?.offsetTop ?? 0, behavior: 'smooth' });
      } else {
        setSubmitError(data.message || 'Unable to submit your enquiry. Please try again later.');
      }
    } catch (err) {
      console.error('Error submitting home enquiry:', err);
      setSubmitError('Unable to submit your enquiry. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    {
      title: 'ePublisher',
      subtitle: 'Online School Communication Platform',
      image: ePublisherImage,
      description: 'Modern digital communication tools for schools to connect with families, students, and staff.',
      link: 'https://epublisherapp.com/app/'
    },
    {
      title: 'Advertising',
      subtitle: 'School Newsletter Advertising Opportunities',
      image: advertisingImage,
      description: 'Reach your target audience directly through school newsletters and digital platforms.',
      link: '/advertising'
    },
    {
      title: 'Design+Print',
      subtitle: 'Professional Graphic Design & Print Services',
      image: printDesignImage,
      description: 'Custom design and printing services for all your school stationery needs.',
      link: '/design-print'
    }
  ];

  const testimonials = [
    {
      school: 'Cannonvale State School',
      quote: 'ePublisher made the incredibly difficult times of this pandemic, manageable. We will always be grateful to ePublisher!'
    },
    {
      school: 'Benowa State School',
      quote: 'The platform is very easy to use. Support is always on hand to assist with any questions. More and more things are added to make it a one stop place for all information for families.'
    },
    {
      school: 'Salisbury State School',
      quote: 'e-Publisher provides a range of functions that suit communication with our modern, e-connected families. With newsletters, newsflashes, reminders, surveys, permission forms and so much more - we are now operating more efficiently than ever before. The days of lost notes and forgotten paperwork is over. Our families are truly connected and our work systems streamlined.'
    },
    {
      school: 'Sunnybank Hills State School',
      quote: 'Our school have been with Austnews for over 10 years now and we have to say, their ePublisher platform has totally revolutionised the way we communicate with our school community.\n\nFrom the Austnews training to the customer service and the constant innovation and the ease of use, ePublisher has given us that vital instant communication link with our families electronically. It helps celebrate and bring our school spirit to life in full colour – using photos, videos, iMovies and endless information.'
    },
    {
      school: 'Advanced Air Conditioning',
      quote: 'Austnews Advertising has provided our company with the ability to efficiently and effectively deliver our message and branding to our local community. We\'ve now been successfully advertising while supporting our local school for the past 4 years. The team at Austnews is able to provide us with a professionally finished advert that is easy to book, approve, and present to our market. I recommend Austnews and its advertising service to any business or company looking to target family households and other businesses.'
    },
    {
      school: 'Bayside Dance',
      quote: 'We have been advertising with Austnews School Newsletters for over five years, which has allowed us to communicate to school families in our local area on a regular basis. During that time we have seen increased enquiries and engagement with local schools which has nurtured strong community support for Bayside Dance.'
    }
  ];

  const handleServiceClick = (link) => {
    if (link.startsWith('http')) {
      // External link
      window.open(link, '_blank');
    } else {
      // Internal link
      navigate(link);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      {/* Subtle staff login icon - fixed top right */}
      <Tooltip title="Staff Login" placement="left">
        <IconButton
          onClick={() => navigate('/app')}
          sx={{
            position: 'fixed',
            top: 12,
            right: 12,
            zIndex: 1000,
            color: 'rgba(0,0,0,0.2)',
            '&:hover': {
              color: 'rgba(0,0,0,0.5)',
              backgroundColor: 'rgba(0,0,0,0.04)'
            }
          }}
          size="small"
        >
          <LockIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '500px', md: '700px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `url(${handsImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          {/* Large Centered Logo */}
          <Box
            component="img"
            src={logo}
            alt="Austnews Logo"
            sx={{
              maxWidth: { xs: '80%', sm: '70%', md: '600px' },
              height: 'auto',
              mb: 4,
              filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))'
            }}
          />
          
          {/* Tagline */}
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 600,
              color: '#333132',
              fontSize: { xs: '1.1rem', md: '1.5rem' },
              mb: 0,
              letterSpacing: '0.02em'
            }}
          >
            Austnews provides the tools that connect School Communities
          </Typography>
        </Container>
      </Box>

      {/* Quick Links Section */}
      <Box sx={{ py: 4, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: { xs: 2, md: 4 },
              flexWrap: 'wrap'
            }}
          >
            <Button
              onClick={() => {
                const element = document.getElementById('about-us');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              sx={{
                color: '#333132',
                fontSize: { xs: '0.9rem', md: '1rem' },
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: '#FF8C00'
                }
              }}
            >
              About Us
            </Button>
            <Button
              onClick={() => {
                const element = document.getElementById('testimonials');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              sx={{
                color: '#333132',
                fontSize: { xs: '0.9rem', md: '1rem' },
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: '#FF8C00'
                }
              }}
            >
              Testimonials
            </Button>
            <Button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              sx={{
                color: '#333132',
                fontSize: { xs: '0.9rem', md: '1rem' },
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: '#FF8C00'
                }
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Services Section - Right Under Hero */}
      <Box sx={{ py: 6, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6
                    }
                  }}
                  onClick={() => handleServiceClick(service.link)}
                >
                  <CardContent sx={{ flexGrow: 1, p: 4 }}>
                    <Box 
                      sx={{ 
                        mb: 3,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 200
                      }}
                    >
                      <Box
                        component="img"
                        src={service.image}
                        alt={service.title}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 200,
                          objectFit: 'contain'
                        }}
                      />
                    </Box>
                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                      {service.title}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                      {service.subtitle}
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {service.description}
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        mt: 2,
                        backgroundColor: '#FF8C00',
                        '&:hover': {
                          backgroundColor: '#E67E00'
                        }
                      }}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* About Section */}
      <Box id="about-us" sx={{ py: 8, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom 
            sx={{ 
              mb: 4, 
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: '#333132'
            }}
          >
            Austnews, Who are We?
          </Typography>
          
          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              fontSize: { xs: '0.95rem', md: '1rem' }, 
              lineHeight: 1.6, 
              mb: 2,
              color: '#333132'
            }}
          >
            <strong>Austnews (short for Australian Newsletter Services) is a communication service schools use to connect with their families, students, staff and the local community.</strong>
          </Typography>

          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              fontSize: { xs: '0.95rem', md: '1rem' }, 
              lineHeight: 1.6, 
              mb: 2,
              color: '#333132'
            }}
          >
            Since 1978, Austnews has been an Australian family owned and operated business providing hundreds of schools across Australia, and now New Zealand. Providing newsletter templates, printing and stationery services. With the ever-changing world of technology, our business model is continually evolving to provide schools with the latest technology. Including the delivery of a superior digital service known as{' '}
            <Box
              component="a"
              href="https://epublisher.net.au/app/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: '#FF8C00',
                textDecoration: 'none',
                fontWeight: 600,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              ePublisher
            </Box>
            .
          </Typography>

          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              fontSize: { xs: '0.95rem', md: '1rem' }, 
              lineHeight: 1.6, 
              mb: 4,
              color: '#333132'
            }}
          >
            Our team is comprised of dedicated professionals with a continued vision to innovate, design and build bespoke communication tools. Aiming to maintain an open gateway between the school classroom and the family home. We are teachers, marketers, graphic designers, programmers and parents who understand the importance and value of communication and its role in education.
          </Typography>

          <Typography 
            variant="h4" 
            component="h3" 
            gutterBottom 
            sx={{ 
              mt: 6, 
              mb: 3, 
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              color: '#333132'
            }}
          >
            Austnews Advertising
          </Typography>

          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              fontSize: { xs: '0.95rem', md: '1rem' }, 
              lineHeight: 1.6, 
              mb: 2,
              color: '#333132'
            }}
          >
            Austnews supplies schools with newsletters and communication services for a low fee. All thanks to the small business, companies and large corporations who choose to advertise. The advertising fee these businesses pay covers the ongoing design, print, hosting and tech support costs required to keep these tools relevant and available. Some might even say their advertising is a form of sponsorship and community support for their local school.
          </Typography>

          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              fontSize: { xs: '0.95rem', md: '1rem' }, 
              lineHeight: 1.6,
              color: '#333132'
            }}
          >
            Over the past 42 years, we have seen several thousand Australian and International businesses advertise with Austnews. Not only to show their support but to reach their target audience in the most direct form possible; straight into their households and email addresses. If you're looking to support your local school(s) get in touch with us. With a professional team of advertising consultants, your enquiry will be answered promptly.
          </Typography>

          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: { xs: '0.95rem', md: '1rem' }, 
              lineHeight: 1.6,
              color: '#333132',
              mt: 2
            }}
          >
            Want to know more? Follow our{' '}
            <Box
              component="a"
              href="https://www.facebook.com/austnewsadvertising"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: '#FF8C00',
                textDecoration: 'none',
                fontWeight: 600,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Advertising Facebook page
            </Box>
            {' '}for the latest advertising tips, schools, promotions and updates. Alternatively you can{' '}
            <Box
              component="a"
              onClick={() => navigate('/advertising')}
              sx={{
                color: '#FF8C00',
                textDecoration: 'none',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              view our website
            </Box>
            {' '}to help you better understand how Austnews Advertising can benefit your business and your school.
          </Typography>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box id="testimonials" sx={{ py: 8, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 6, fontWeight: 600 }}>
            Testimonials
          </Typography>
          
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderLeft: '4px solid #FF8C00'
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#FF8C00' }}>
                    {testimonial.school}
                  </Typography>
                  <Typography variant="body1" sx={{ fontStyle: 'italic', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                    "{testimonial.quote}"
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Box id="contact" sx={{ py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4, fontWeight: 600 }}>
            Contact Us
          </Typography>
          
          <Paper elevation={3} sx={{ p: 4 }}>
            {submitSuccess && (
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSubmitSuccess(false)}>
                Thank you for your enquiry. We have received your message and will be in touch soon.
              </Alert>
            )}
            {submitError && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError(null)}>
                {submitError}
              </Alert>
            )}
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Contact Name"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="School or Business Name"
                    name="schoolOrBusinessName"
                    value={formData.schoolOrBusinessName}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Phone Number"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Email Address"
                    name="emailAddress"
                    value={formData.emailAddress}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    select
                    label="Please select the service relative to your enquiry"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="ePublisher">ePublisher Platform</MenuItem>
                    <MenuItem value="advertising">Advertising</MenuItem>
                    <MenuItem value="printDesign">Design+Print</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={4}
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    select
                    label="How did you hear about Austnews?"
                    name="howDidYouHear"
                    value={formData.howDidYouHear}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="google">Google</MenuItem>
                    <MenuItem value="facebook">Facebook</MenuItem>
                    <MenuItem value="referral">Referral</MenuItem>
                    <MenuItem value="school">Current School</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={submitting}
                    sx={{
                      backgroundColor: '#FF8C00',
                      '&:hover': {
                        backgroundColor: '#E67E00'
                      },
                      py: 1.5
                    }}
                  >
                    {submitting ? <CircularProgress size={26} color="inherit" /> : 'Submit'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, backgroundColor: '#2C3E50', color: 'white', textAlign: 'center' }}>
        <Container>
          <Typography variant="body2">
            Copyright Australian Newsletter Services Pty Ltd 2016-2024 | All Rights Reserved
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            QAssure Number: 11954 | Accredited GITC Supplier: Q-6019 | ABN 24 010 367 478
          </Typography>
          <Typography sx={{ mt: 1, fontSize: '0.6rem', opacity: 0.4, letterSpacing: '0.05em' }}>
            MIS
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;

